import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { 
  CreditCard, 
  Settings, 
  TestTube, 
  Save, 
  AlertCircle,
  CheckCircle,
  DollarSign,
  Globe,
  Shield
} from 'lucide-react';
import { adminAPI } from '../../services/api';
import LoadingSpinner from '../LoadingSpinner';
import toast from 'react-hot-toast';

const StripeSettings = () => {
  const [isEditing, setIsEditing] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const queryClient = useQueryClient();

  // Fetch Stripe settings
  const { data: settings, isLoading, error } = useQuery(
    'stripeSettings',
    () => adminAPI.getStripeSettings().then(res => res.data),
    {
      onError: (err) => {
        toast.error('Failed to load Stripe settings');
      }
    }
  );

  // Update settings mutation
  const updateSettingsMutation = useMutation(
    (updatedSettings) => adminAPI.updateStripeSettings(updatedSettings),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('stripeSettings');
        setIsEditing(false);
        toast.success('Stripe settings updated successfully');
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || 'Failed to update settings');
      }
    }
  );

  // Test connection mutation
  const testConnectionMutation = useMutation(
    () => adminAPI.testStripeConnection(),
    {
      onSuccess: (data) => {
        setTestResult({ success: true, data: data.data });
        toast.success('Stripe connection test successful');
      },
      onError: (error) => {
        setTestResult({ success: false, error: error.response?.data?.error });
        toast.error('Stripe connection test failed');
      }
    }
  );

  const handleSave = (formData) => {
    updateSettingsMutation.mutate(formData);
  };

  const handleTestConnection = () => {
    testConnectionMutation.mutate();
  };

  if (isLoading) return <LoadingSpinner />;
  if (error) return <div className="text-red-500">Error loading settings</div>;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <CreditCard className="h-8 w-8 text-discord-blurple" />
          <h2 className="text-2xl font-bold text-discord-lightest">Stripe Payment Settings</h2>
        </div>
        <div className="flex space-x-3">
          <button
            onClick={handleTestConnection}
            disabled={testConnectionMutation.isLoading}
            className="btn btn-secondary flex items-center space-x-2"
          >
            <TestTube className="h-4 w-4" />
            <span>Test Connection</span>
          </button>
          <button
            onClick={() => setIsEditing(!isEditing)}
            className="btn btn-primary flex items-center space-x-2"
          >
            <Settings className="h-4 w-4" />
            <span>{isEditing ? 'Cancel' : 'Edit Settings'}</span>
          </button>
        </div>
      </div>

      {/* Connection Test Result */}
      {testResult && (
        <div className={`p-4 rounded-lg border ${
          testResult.success 
            ? 'bg-green-900/20 border-green-500 text-green-400' 
            : 'bg-red-900/20 border-red-500 text-red-400'
        }`}>
          <div className="flex items-center space-x-2">
            {testResult.success ? (
              <CheckCircle className="h-5 w-5" />
            ) : (
              <AlertCircle className="h-5 w-5" />
            )}
            <span className="font-medium">
              {testResult.success ? 'Connection Successful' : 'Connection Failed'}
            </span>
          </div>
          {testResult.success && testResult.data && (
            <div className="mt-2 text-sm">
              <p>Account ID: {testResult.data.account.id}</p>
              <p>Country: {testResult.data.account.country}</p>
              <p>Currency: {testResult.data.account.default_currency}</p>
              <p>Charges Enabled: {testResult.data.account.charges_enabled ? 'Yes' : 'No'}</p>
            </div>
          )}
          {testResult.error && (
            <p className="mt-2 text-sm">{testResult.error}</p>
          )}
        </div>
      )}

      {isEditing ? (
        <StripeSettingsForm 
          settings={settings} 
          onSave={handleSave} 
          onCancel={() => setIsEditing(false)}
          isLoading={updateSettingsMutation.isLoading}
        />
      ) : (
        <StripeSettingsDisplay settings={settings} />
      )}

      {/* Server Plans */}
      <ServerPlansSection settings={settings} />
    </div>
  );
};

const StripeSettingsForm = ({ settings, onSave, onCancel, isLoading }) => {
  const [formData, setFormData] = useState({
    currency: settings?.currency || 'usd',
    businessName: settings?.businessName || '',
    businessEmail: settings?.businessEmail || '',
    taxEnabled: settings?.taxEnabled || false,
    taxRate: settings?.taxRate || 0,
    trialDays: settings?.trialDays || 7,
    webhookUrl: settings?.webhookUrl || ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  return (
    <form onSubmit={handleSubmit} className="bg-discord-darker p-6 rounded-lg border border-discord-dark">
      <h3 className="text-lg font-semibold text-discord-lightest mb-4">Payment Configuration</h3>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-discord-light text-sm font-medium mb-2">
            Currency
          </label>
          <select
            name="currency"
            value={formData.currency}
            onChange={handleChange}
            className="w-full input-field"
          >
            <option value="usd">USD - US Dollar</option>
            <option value="eur">EUR - Euro</option>
            <option value="gbp">GBP - British Pound</option>
            <option value="cad">CAD - Canadian Dollar</option>
            <option value="aud">AUD - Australian Dollar</option>
          </select>
        </div>

        <div>
          <label className="block text-discord-light text-sm font-medium mb-2">
            Business Name
          </label>
          <input
            type="text"
            name="businessName"
            value={formData.businessName}
            onChange={handleChange}
            className="w-full input-field"
            placeholder="Your Business Name"
          />
        </div>

        <div>
          <label className="block text-discord-light text-sm font-medium mb-2">
            Business Email
          </label>
          <input
            type="email"
            name="businessEmail"
            value={formData.businessEmail}
            onChange={handleChange}
            className="w-full input-field"
            placeholder="admin@yourbusiness.com"
          />
        </div>

        <div>
          <label className="block text-discord-light text-sm font-medium mb-2">
            Trial Days
          </label>
          <input
            type="number"
            name="trialDays"
            value={formData.trialDays}
            onChange={handleChange}
            className="w-full input-field"
            min="0"
            max="30"
          />
        </div>

        <div>
          <label className="block text-discord-light text-sm font-medium mb-2">
            Webhook URL
          </label>
          <input
            type="url"
            name="webhookUrl"
            value={formData.webhookUrl}
            onChange={handleChange}
            className="w-full input-field"
            placeholder="https://yourdomain.com/api/stripe/webhook"
          />
        </div>
      </div>

      <div className="mt-6">
        <div className="flex items-center space-x-3">
          <input
            type="checkbox"
            id="taxEnabled"
            name="taxEnabled"
            checked={formData.taxEnabled}
            onChange={handleChange}
            className="rounded border-discord-dark bg-discord-darker text-discord-blurple focus:ring-discord-blurple"
          />
          <label htmlFor="taxEnabled" className="text-discord-light">
            Enable Tax Collection
          </label>
        </div>
        
        {formData.taxEnabled && (
          <div className="mt-3">
            <label className="block text-discord-light text-sm font-medium mb-2">
              Tax Rate (%)
            </label>
            <input
              type="number"
              name="taxRate"
              value={formData.taxRate * 100}
              onChange={(e) => setFormData(prev => ({
                ...prev,
                taxRate: parseFloat(e.target.value) / 100
              }))}
              className="w-full input-field"
              min="0"
              max="100"
              step="0.1"
            />
          </div>
        )}
      </div>

      <div className="flex justify-end space-x-3 mt-6">
        <button
          type="button"
          onClick={onCancel}
          className="btn btn-secondary"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={isLoading}
          className="btn btn-primary flex items-center space-x-2"
        >
          <Save className="h-4 w-4" />
          <span>{isLoading ? 'Saving...' : 'Save Settings'}</span>
        </button>
      </div>
    </form>
  );
};

const StripeSettingsDisplay = ({ settings }) => {
  if (!settings) return <div>No settings found</div>;

  return (
    <div className="bg-discord-darker p-6 rounded-lg border border-discord-dark">
      <h3 className="text-lg font-semibold text-discord-lightest mb-4">Current Settings</h3>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="flex items-center space-x-3">
          <DollarSign className="h-5 w-5 text-discord-blurple" />
          <div>
            <p className="text-discord-light text-sm">Currency</p>
            <p className="text-discord-lightest font-medium">{settings.currency?.toUpperCase()}</p>
          </div>
        </div>

        <div className="flex items-center space-x-3">
          <Globe className="h-5 w-5 text-discord-blurple" />
          <div>
            <p className="text-discord-light text-sm">Business Name</p>
            <p className="text-discord-lightest font-medium">{settings.businessName}</p>
          </div>
        </div>

        <div className="flex items-center space-x-3">
          <Shield className="h-5 w-5 text-discord-blurple" />
          <div>
            <p className="text-discord-light text-sm">Tax Collection</p>
            <p className="text-discord-lightest font-medium">
              {settings.taxEnabled ? `${(settings.taxRate * 100).toFixed(1)}%` : 'Disabled'}
            </p>
          </div>
        </div>

        <div className="flex items-center space-x-3">
          <Settings className="h-5 w-5 text-discord-blurple" />
          <div>
            <p className="text-discord-light text-sm">Trial Days</p>
            <p className="text-discord-lightest font-medium">{settings.trialDays} days</p>
          </div>
        </div>
      </div>
    </div>
  );
};

const ServerPlansSection = ({ settings }) => {
  return (
    <div className="bg-discord-darker p-6 rounded-lg border border-discord-dark">
      <h3 className="text-lg font-semibold text-discord-lightest mb-4">Server Subscription Plans</h3>
      
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {settings?.serverPlans?.map((plan, index) => (
          <div key={index} className="bg-discord-darkest p-4 rounded-lg border border-discord-dark">
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-discord-lightest font-semibold">{plan.name}</h4>
              <span className={`px-2 py-1 rounded text-xs ${
                plan.isActive ? 'bg-green-900 text-green-400' : 'bg-red-900 text-red-400'
              }`}>
                {plan.isActive ? 'Active' : 'Inactive'}
              </span>
            </div>
            
            <div className="space-y-2">
              <p className="text-2xl font-bold text-discord-lightest">
                ${(plan.price / 100).toFixed(2)}
                <span className="text-sm text-discord-light">/{plan.interval}</span>
              </p>
              
              <div className="space-y-1">
                <p className="text-discord-light text-sm">Max Users: {plan.maxUsers === -1 ? 'Unlimited' : plan.maxUsers}</p>
                <p className="text-discord-light text-sm">Max Servers: {plan.maxServers}</p>
              </div>
              
              <div className="mt-3">
                <p className="text-discord-light text-sm font-medium mb-1">Features:</p>
                <ul className="text-discord-light text-xs space-y-1">
                  {plan.features?.map((feature, idx) => (
                    <li key={idx} className="flex items-center space-x-2">
                      <CheckCircle className="h-3 w-3 text-green-400" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default StripeSettings;
